'use client'

import { useRef } from 'react'
import { motion, useInView } from 'motion/react'

const skillCategories = [
	{
		id: 'languages',
		title: 'Languages',
		skills: [
			{ name: 'Rust', level: 90 },
			{ name: 'TypeScript', level: 90 },
			{ name: 'Python', level: 90 },
			{ name: 'C#', level: 80 },
			{ name: 'Go', level: 75 },
			{ name: 'Swift', level: 45 }
		]
	},
	{
		id: 'frameworks',
		title: 'Frameworks',
		skills: [
			{ name: 'Next.js', level: 97 },
			{ name: 'Tailwind CSS', level: 95 },
			{ name: 'Prisma', level: 90 },              // TypeScript ORM
			{ name: 'Flutter', level: 80 },
			{ name: 'Tauri', level: 75 },               // Rust/TS
			{ name: 'Actix', level: 70 },               // Rust web
		]
	},
	{
		id: 'tools',
		title: 'Tools & Cloud',
		skills: [
			{ name: 'Git', level: 90 },
			{ name: 'Docker', level: 88 },
			{ name: 'Cloudflare', level: 85 },
			{ name: 'PostgreSQL', level: 85 },
			{ name: 'MongoDB', level: 82 },
			{ name: 'Firebase', level: 67 }
		]
	}
]

const additionalSkills = [
	// AI & Data Science
	{ name: 'Machine Learning', highlighted: true },
	{ name: 'Artificial Intelligence', highlighted: true },
	{ name: 'Computer Vision', highlighted: true },
	{ name: 'Data Analysis', highlighted: true },
	{ name: 'Data Visualization', highlighted: false },
	{ name: 'Prompt Patterns', highlighted: false },

	// Cloud, DevOps & Infrastructure
	{ name: 'Cloudflare Zero Trust', highlighted: true },
	{ name: 'Docker', highlighted: false },
	{ name: 'Kubernetes', highlighted: false },

	// Backend & APIs
	{ name: 'REST APIs', highlighted: false },
	{ name: 'WebSockets', highlighted: false },
	{ name: 'gRPC', highlighted: false },
	{ name: 'Microservices', highlighted: false },

	// Systems, Testing & Hardware
	{ name: 'System Design', highlighted: false },
	{ name: 'Testing', highlighted: false },
	{ name: 'Raspberry Pi', highlighted: false },
	{ name: 'ESP32', highlighted: false },

	// Soft/Meta Skills
	{ name: 'Complex Problem Solving', highlighted: true },
	{ name: 'Critical Thinking', highlighted: true }
]
const Skills = () => {
	const ref = useRef(null)
	const isInView = useInView(ref, { once: false, amount: 0.2 })

	const containerVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				staggerChildren: 0.1
			}
		}
	} as const

	const itemVariants = {
		hidden: { opacity: 0, y: 30 },
		visible: {
			opacity: 1,
			y: 0,
			transition: {
				duration: 0.6,
				ease: [0.22, 1, 0.36, 1] as const
			}
		}
	} as const

	return (
		<section id="skills" className="py-24 md:py-32">
			<div className="section-container">
				<motion.div
					ref={ref}
					variants={containerVariants}
					initial="hidden"
					animate={isInView ? 'visible' : 'hidden'}
					className="space-y-16"
				>
					{/* Header */}
					<motion.div variants={itemVariants} className="text-center max-w-2xl mx-auto">
						<h2 className="text-3xl md:text-5xl font-bold mb-6">
							Technical <span className="text-gradient">Skills</span>
						</h2>
						<p className="text-lg text-gray-600 dark:text-gray-400">
							A comprehensive overview of my technical expertise and proficiency.
						</p>
					</motion.div>

					{/* Skills grid */}
					<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
						{skillCategories.map((category, categoryIndex) => (
							<motion.div
								key={category.id}
								variants={itemVariants}
								className="glass-card p-6 rounded-3xl"
							>
								<h3 className="text-xl font-bold mb-6">{category.title}</h3>
								<div className="space-y-4">
									{category.skills.map((skill, skillIndex) => (
										<div key={skill.name} className="space-y-2">
											<div className="flex justify-between text-sm">
												<span className="font-medium">{skill.name}</span>
												<span className="text-gray-500">{skill.level}%</span>
											</div>
											<div className="h-1.5 w-full bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
												<motion.div
													className="h-full bg-linear-to-r from-green-600 to-emerald-400 rounded-full"
													initial={{ width: 0 }}
													whileInView={{ width: `${skill.level}%` }}
													viewport={{ once: false, amount: 0.5 }}
													transition={{ duration: 1, delay: categoryIndex * 0.1 + skillIndex * 0.05, ease: [0.22, 1, 0.36, 1] }}
												/>
											</div>
										</div>
									))}
								</div>
							</motion.div>
						))}
					</div>

					{/* Additional skills */}
					<motion.div variants={itemVariants} className="text-center">
						<h3 className="text-lg font-bold mb-6">Additional Skills</h3>
						<div className="flex flex-wrap justify-center gap-2 max-w-4xl mx-auto">
							{additionalSkills.map((skill, index) => (
								<motion.span
									key={skill.name}
									initial={{ opacity: 0, scale: 0.9 }}
									whileInView={{ opacity: 1, scale: 1 }}
									viewport={{ once: false }}
									transition={{ duration: 0.3, delay: index * 0.02 }}
									className={`px-4 py-2 rounded-full text-sm font-medium ${
										skill.highlighted
											? 'animate-border-glow bg-white/90 dark:bg-[#1a1a1a]'
											: 'glass-card'
									}`}
								>
									{skill.name}
								</motion.span>
							))}
						</div>
					</motion.div>
				</motion.div>
			</div>
		</section>
	)
}

export default Skills
